import { SettingsManager } from "@viamap/viamap2-common";
import { GenerateGeomUtils } from "./GenerateGeomUtils";

/* oxlint-disable no-restricted-imports */
import geodesicArea from "@turf/area";
import geodesicLength from "@turf/length";


export function areaByUserPreference(geo: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry) {
   if (SettingsManager.getSystemSetting("geodesicCalculations", false, true)) {
      return geodesicArea(geo);
   } else {
      return planarArea(geo);
   }
}


export function lengthByUserPreference(geo: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry) {
   if (SettingsManager.getSystemSetting("geodesicCalculations", false, true)) {
      if (geo.type !== "FeatureCollection" && geo.type !== "Feature") {
         geo = { type: "Feature", geometry: geo, properties: {} };
      }
      return geodesicLength(geo);
   } else {
      return planarLength(geo) / 1000;
   }
}

export function planarArea(geo: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry) {
   return reduceFeature(geo, (ft) => planarFeatureArea(_transformer(ft)));
}

export function planarLength(geo: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry) {
   return reduceFeature(geo, (ft) => planarFeatureLength(_transformer(ft)));
}

function _eqC(a?: number[], b?: number[]) {
   return a && b && a[0] === b[0] && a[1] === b[1];
}

function calculateShoelaceArea(pointArr: number[][]) {
   let FL_EQ = _eqC(pointArr[0], pointArr.at(-1))
   if (!FL_EQ) {
      pointArr.push(pointArr[0]);
   }
   let area = 0;
   for (let i = 0; i < pointArr.length - 1; i++) {
      const p1 = pointArr[i];
      const p2 = pointArr[i + 1];
      area += (p1[0] * p2[1]) - (p2[0] * p1[1]);
   }
   return Math.abs(area / 2);
}

function calculatePointListLength(pointArr: number[][], autoClose: boolean = false) {
   let FL_EQ = _eqC(pointArr[0], pointArr.at(-1))
   if (!FL_EQ && autoClose) {
      pointArr.push(pointArr[0]);
   }

   let length = 0;
   for (let i = 0; i < pointArr.length - 1; i++) {
      const p1 = pointArr[i];
      const p2 = pointArr[i + 1];
      length += Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));
   }

   return length;
}

function isWeakFeatureCollection(geo: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry): geo is GeoJSON.FeatureCollection {
   return geo.type === "FeatureCollection" || ("features" in geo && Array.isArray(geo.features));
}

function reduceFeature(geo: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry, fn: (feature: GeoJSON.Feature) => number) {
   if (isWeakFeatureCollection(geo)) {
      return geo.features.map(fn).reduce((a, b) => a + b, 0);
   } else if (geo.type === "Feature") {
      return fn(geo);
   } else {
      return fn({ type: "Feature", geometry: geo, properties: {} });
   }
}

function _transformer(geo: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry) {
   let x = GenerateGeomUtils.geoJsonCoordsTransformer(geo, (coord: any[]) => {
      // Coords is lngLat
      let utmCoord = GenerateGeomUtils.convertFromLatLngtoUTM2([Number(coord[1]), Number(coord[0])]);
      return utmCoord as [number, number];
   });
   return x;
}

function planarFeatureArea(feature: GeoJSON.Feature) {
   if (feature.geometry) {
      return planarPolygonArea(feature.geometry);
   }
   return 0;
}

function planarFeatureLength(feature: GeoJSON.Feature) {
   if (feature.geometry) {
      return planarGeometryLength(feature.geometry);
   }
   return 0;
}

function planarGeometryLength(geometry: GeoJSON.Geometry) {
   if (geometry.type === "LineString") {
      return calculatePointListLength(geometry.coordinates);
   } else if (geometry.type === "MultiLineString") {
      return geometry.coordinates.map(line => calculatePointListLength(line)).reduce((a, b) => a + b, 0);
   } else if (geometry.type === "Polygon") {
      let autoClose = true
      return geometry.coordinates.map(ring => calculatePointListLength(ring, autoClose)).reduce((a, b) => a + b, 0);
   } else if (geometry.type === "MultiPolygon") {
      let autoClose = true
      return geometry.coordinates.map(polygon => polygon.map(ring => calculatePointListLength(ring, autoClose)).reduce((a, b) => a + b, 0)).reduce((a, b) => a + b, 0);
   }
   return 0;
}

function planarPolygonArea(geometry: GeoJSON.Geometry) {
   if (geometry.type === "Polygon") {
      return geometry.coordinates.map(ring => calculateShoelaceArea(ring)).reduce((a, b) => a - b);
   } else if (geometry.type === "MultiPolygon") {
      return geometry.coordinates.map(polygon => polygon.map(ring => calculateShoelaceArea(ring)).reduce((a, b) => a - b)).reduce((a, b) => a + b, 0);
   }
   return 0;
}